import { ButtonBase, Chip, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { If } from "lib/@components/switch"
import { resolveCommentAnchorPresentation } from "routes/schedule/plugins/share-link-comments/share-link-comment-presenters"

export function ShareLinkCommentAnchorHeader({ comment, schedule, onJump }) {
    const theme = useTheme()
    const presentation = resolveCommentAnchorPresentation(comment, schedule)
    const anchor = comment?.anchor ?? {}
    const targetText = anchor.targetText ?? ""

    return (
        <Stack spacing={0.5} sx={{ width: 1 }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: 1 }}>
                <ButtonBase onClick={jump} data-cy={`share-link-comment-anchor-${comment.id}`} sx={getBodySx()}>
                    <Iconify icon={presentation.icon} sx={getIconSx()} />
                    <Typography variant="caption" color="text.primary" noWrap sx={getTitleSx()}>
                        {presentation.fallbackLabel}
                    </Typography>
                    <If value={isScheduleLevelComment()}>
                        <Chip
                            size="small"
                            label="Schedule level"
                            data-cy={`share-link-comment-schedule-level-${comment.id}`}
                            sx={getChipSx()}
                        />
                    </If>
                    <If value={!!anchor.code}>
                        <Typography variant="caption" color="text.disabled" noWrap sx={{ flexShrink: 0 }}>
                            #{anchor.code}
                        </Typography>
                    </If>
                    <If value={!!presentation.partLabel}>
                        <Chip
                            size="small"
                            label={presentation.partLabel}
                            data-cy={`share-link-comment-part-${comment.id}`}
                            sx={getChipSx()}
                        />
                    </If>
                    <If value={!!presentation.isDifferentVersion}>
                        <Chip size="small" label={presentation.scheduleVersionLabel} sx={getChipSx()} />
                    </If>
                    <If value={!!presentation.statusLabel}>
                        <Chip size="small" color="warning" label={presentation.statusLabel} sx={getChipSx()} />
                    </If>
                    <Typography variant="caption" color="text.disabled" noWrap sx={getVersionCaptionSx()}>
                        {presentation.commentVersionLabel}
                    </Typography>
                </ButtonBase>
                <Tooltip title="Open comment target">
                    <IconButton
                        size="small"
                        onClick={jump}
                        data-cy={`share-link-comment-jump-${comment.id}`}
                        aria-label="Open comment target"
                        sx={getJumpButtonSx()}
                    >
                        <Iconify icon="mdi:arrow-right-circle-outline" />
                    </IconButton>
                </Tooltip>
            </Stack>
            <If value={!!anchor.location}>
                <Stack
                    direction="row"
                    spacing={0.25}
                    alignItems="center"
                    data-cy={`share-link-comment-location-${comment.id}`}
                >
                    <Iconify icon="mdi:map-marker-outline" sx={getLocationIconSx()} />
                    <Typography variant="caption" color="text.disabled" noWrap sx={{ minWidth: 0 }}>
                        {anchor.location}
                    </Typography>
                </Stack>
            </If>
            {!!targetText && (
                <Typography variant="caption" color="text.secondary" noWrap sx={getQuoteSx()}>
                    “{targetText}”
                </Typography>
            )}
        </Stack>
    )

    function getLocationIconSx() {
        return {
            flexShrink: 0,
            width: 14,
            height: 14,
            color: theme.palette.text.disabled,
        }
    }

    function getBodySx() {
        return {
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 0.5,
            py: 0.25,
            borderRadius: 1,
            cursor: "pointer",
            textAlign: "left",
            justifyContent: "flex-start",
            transition: "background-color 0.2s ease",
            "&:hover": {
                bgcolor: "action.hover",
            },
        }
    }

    function getTitleSx() {
        return {
            fontWeight: 600,
            minWidth: 0,
        }
    }

    function getJumpButtonSx() {
        return {
            flexShrink: 0,
            width: 26,
            height: 26,
            color: "primary.main",
            bgcolor: "background.neutral",
            "&:hover": {
                bgcolor: "action.hover",
            },
        }
    }

    function getIconSx() {
        return {
            flexShrink: 0,
            width: 18,
            height: 18,
            color: theme.palette.text.secondary,
        }
    }

    function getChipSx() {
        return {
            height: 18,
            flexShrink: 0,
            "& .MuiChip-label": {
                px: 0.75,
                fontSize: 10,
                fontWeight: 600,
                lineHeight: "14px",
            },
        }
    }

    // The anchored quote reads like a pinned excerpt: left accent bar in the highlight colour over
    // a neutral block, matching the in-document highlight it points at.
    function getQuoteSx() {
        return {
            display: "block",
            maxWidth: 1,
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            borderRadius: 0,
            bgcolor: "background.neutral",
            px: 1,
            py: 0.5,
            mt: 0.25,
        }
    }

    function getVersionCaptionSx() {
        return {
            ml: "auto",
            pl: 0.5,
            flexShrink: 0,
        }
    }

    function jump() {
        onJump?.(comment)
    }

    function isScheduleLevelComment() {
        return anchor.type === "schedule"
    }
}
